import type { ClipboardEvent } from 'react';
import { notifyError } from '@/core/notify/notify';

/** Shared by every attach surface in the Application Testing feature: raising a ticket, a comment, and the ticket's own attachments. */
export const ALLOWED_EXTENSIONS = ['.doc', '.docx', '.pdf', '.jpg', '.png', '.jpeg', '.txt', '.xls', '.xlsx', '.csv'];
export const MAX_FILE_BYTES = 2 * 1024 * 1024;
export const MAX_FILES = 10;

/** Validates `incoming` against the shared limits and appends the valid ones to `current`, toasting on rejection. */
export function appendValidatedFiles(current: File[], incoming: File[]): File[] {
  const next = [...current];

  incoming.forEach((file) => {
    const extension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase() : '';
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      notifyError(`${file.name} is not a supported file type.`);
    } else if (file.size === 0) {
      notifyError(`${file.name} is empty.`);
    } else if (file.size > MAX_FILE_BYTES) {
      notifyError(`${file.name} is larger than 2 MB.`);
    } else if (next.length >= MAX_FILES) {
      notifyError(`Attach at most ${MAX_FILES} files.`);
    } else if (!next.some((f) => f.name === file.name && f.size === file.size)) {
      next.push(file);
    }
  });

  return next;
}

/**
 * Pulls the files out of a clipboard paste (Ctrl+V): a screenshot copied from the OS, a paint tool or another app,
 * or any file copied in File Explorer/Finder (a PDF, a spreadsheet, a Word document...). The caller runs them
 * through `appendValidatedFiles`, so an unsupported type is toasted rather than silently dropped. Plain-text pastes
 * have no `kind: 'file'` items and resolve to an empty list, so the caller's default paste behaviour (inserting the
 * text) is left alone.
 */
export function filesFromClipboard(event: ClipboardEvent<HTMLElement>): File[] {
  const items = event.clipboardData?.items;
  if (!items) return [];

  const files: File[] = [];
  Array.from(items).forEach((item) => {
    if (item.kind !== 'file') return;
    const file = item.getAsFile();
    if (!file) return;
    files.push(isUnnamedClipboardImage(file) ? renamePastedImage(file) : file);
  });
  return files;
}

/** A screenshot on the clipboard has no file name of its own - browsers call it "image.png" (or nothing). */
const isUnnamedClipboardImage = (file: File) =>
  file.type.startsWith('image/') && (!file.name || /^image\.\w+$/i.test(file.name));

/** Gives each pasted screenshot a distinct name so the "already attached" de-dupe in `appendValidatedFiles` never
 *  mistakes two different pastes for one file. */
function renamePastedImage(file: File): File {
  const extension = file.type.split('/')[1] || 'png';
  return new File([file], `pasted-image-${Date.now()}.${extension}`, { type: file.type });
}
